import { useEffect } from 'react';
import { Box, TextField, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../../api/userApi';
import { useSnackbar } from '../Snackbar';
import { useAuth } from '../../context/AuthContext';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isAuthenticated } = useAuth();
  const email = searchParams.get('email');
  const resetToken = searchParams.get('token');

  useEffect(() => {
    if (!email?.trim() || !resetToken?.trim()) {
      navigate('/');
    }
  }, [email, resetToken, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/patient', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const formik = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: Yup.object({
      password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/\d/, 'Password must contain at least one number')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          email: email,
          resetToken: resetToken,
          newPassword: values.password,
        };

        const res = await resetPassword(payload);
        if (res.data.succeeded) {
          showSnackbar(res.data.messages[0], 'success');
          navigate('/');
        } else {
          showSnackbar(res.data.messages[0], 'error');
        }
      } catch (err) {
        showSnackbar(err.response?.data?.message || 'Password reset failed', 'error');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Typography variant="h5" mb={2}>
        Reset Password
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          label="New Password"
          type="password"
          {...formik.getFieldProps('password')}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          {...formik.getFieldProps('confirmPassword')}
          error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          margin="normal"
        />
        <Button
          fullWidth
          variant="contained"
          type="submit"
          sx={{ mt: 2 }}
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? 'Resetting...' : 'Reset'}
        </Button>
      </form>
    </Box>
  );
}
